import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Play, Trash2, Eye, Loader2, FileText, History, Briefcase } from 'lucide-react';
import {
  listBaselineReports,
  deleteBaselineReport,
} from '../services/accuracyBackendApi';
import { formatAccuracyStatusLabel } from '../services/accuracyStatusLabels';
import { buildAccuracyReportDetailPath } from '../services/accuracyReportPaths';
import { getReportScopeLabels } from '../components/AccuracyReportFiltersSummary';
import {
  formatGuidelineWithVersion,
  useAccuracyScopeMetadata,
} from '../services/accuracyScopeMetadata';
import { AccuracyReportListFilters } from '../components/AccuracyReportListFilters';
import { METRIC_BALANCE_LABEL } from '../components/AccuracyMetricsSection';
import {
  EMPTY_REPORT_LIST_FILTER,
  filterSavedReports,
} from '../services/accuracyReportListFilter';
import './AccuracyReportPage.css';
import './AccuracyTuningPages.css';

function fmtPct(x: number | null | undefined): string {
  if (x == null) return '—';
  return `${(x * 100).toFixed(1)}%`;
}

/** Map accuracy report status to the JobList status-style class. */
function reportStatusClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'running':
      return 'processing';
    case 'queued':
      return 'pending';
    case 'failed':
      return 'failed';
    default:
      return 'pending';
  }
}

function reportStatusText(status: string): string {
  return formatAccuracyStatusLabel(status);
}

const AccuracyReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [savedReports, setSavedReports] = useState<any[]>([]);
  const [listFilter, setListFilter] = useState(EMPTY_REPORT_LIST_FILTER);
  const scopeMetadata = useAccuracyScopeMetadata();

  const filteredReports = useMemo(
    () => filterSavedReports(savedReports, listFilter),
    [savedReports, listFilter]
  );

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refreshReports = useCallback(() => {
    (async () => {
      const reports = await listBaselineReports();
      setSavedReports(reports);
    })();
  }, []);

  const handleDeleteReport = useCallback(
    async (report: any) => {
      if (!report?.id) return;
      const confirmed = window.confirm(
        `レポート「${report.name || report.id}」を削除しますか？この操作は取り消せません。`
      );
      if (!confirmed) return;

      try {
        setDeletingId(report.id);
        await deleteBaselineReport(report.id);
        setSavedReports((prev) => prev.filter((r) => r.id !== report.id));
      } catch (e) {
        console.error('Failed to delete report:', e);
        alert('レポートの削除に失敗しました。');
      } finally {
        setDeletingId(null);
      }
    },
    []
  );

  useEffect(() => {
    refreshReports();
  }, [refreshReports]);

  useEffect(() => {
    const legacyId = searchParams.get('report_id');
    if (legacyId) {
      navigate(buildAccuracyReportDetailPath(legacyId), { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const hasRunning = savedReports.some(
      (r) => r.status === 'queued' || r.status === 'running'
    );
    if (!hasRunning) return;
    const timer = window.setInterval(refreshReports, 500);
    return () => window.clearInterval(timer);
  }, [savedReports, refreshReports]);

  return (
    <div className="accuracy-page">
      <div className="accuracy-header">
        <div className="accuracy-title">
          <h1>評価レポート</h1>
        </div>
        <div className="accuracy-actions">
          <Link to="/accuracy/run" className="accuracy-button primary">
            <Play size={16} style={{ marginRight: 6 }} />
            レポートを作成
          </Link>
        </div>
      </div>

      <div className="accuracy-card accuracy-report-list-page">
        {savedReports.length === 0 ? (
          <div className="accuracy-empty" style={{ padding: 16 }}>
            <p>まだレポートがありません。</p>
            <Link
              to="/accuracy/run"
              className="accuracy-button primary"
              style={{ marginTop: 16, display: 'inline-flex' }}
            >
              <Play size={16} style={{ marginRight: 6 }} />
              レポートを作成
            </Link>
          </div>
        ) : (
          <>
            <AccuracyReportListFilters
              reports={savedReports}
              filter={listFilter}
              onChange={setListFilter}
              resultCount={filteredReports.length}
              totalCount={savedReports.length}
              metadata={scopeMetadata}
            />
            {filteredReports.length === 0 ? (
              <div className="accuracy-empty" style={{ padding: 16 }}>
                条件に一致するレポートがありません。
              </div>
            ) : (
              <div className="jobs-list">
                {filteredReports.map((r) => {
                  const statusClass = reportStatusClass(r.status);
                  const scope = getReportScopeLabels(r, scopeMetadata);
                  const guidelineText =
                    scope.guidelineRefs
                      .map((ref) => formatGuidelineWithVersion(ref, scopeMetadata))
                      .join('、') || '—';
                  return (
                    <div key={r.id} className={`job-item ${statusClass}`}>
                      <div className="job-header">
                        <div className="job-info">
                          <div className="job-details">
                            <div className="job-name-row">
                              <h3 className="job-name">{r.name}</h3>
                            </div>
                            <div className="job-client-info">
                              <span className="job-guideline">
                                <FileText size={11} />
                                ガイドライン: {guidelineText}
                              </span>
                              <span className="job-asp">
                                <Briefcase size={11} />
                                ジョブ: {scope.jobs}
                              </span>
                              <span className="job-version">
                                <History size={11} />
                                URLステータス: {scope.urlStatuses}
                              </span>
                            </div>
                            <div className="job-meta">
                              <span className="job-start-time">
                                {new Date(r.created_at).toLocaleString('ja-JP')}
                              </span>
                              {r.result && (
                                <span className="job-urls">
                                  違反 {METRIC_BALANCE_LABEL} {fmtPct(r.result.violation.f1)} · URL{' '}
                                  {r.result.url_count} 件
                                </span>
                              )}
                              <span className={`job-status ${statusClass}`}>
                                {reportStatusText(r.status)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="job-actions">
                          <Link
                            to={buildAccuracyReportDetailPath(r.id)}
                            className="btn btn-primary btn-sm"
                          >
                            <Eye size={16} />
                            詳細を表示
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDeleteReport(r)}
                            className="btn btn-danger btn-sm"
                            title="レポートを削除"
                            disabled={deletingId === r.id}
                          >
                            {deletingId === r.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                            削除
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccuracyReportPage;
